// Shared shipment-input form for the rate estimator.
//
// Both surfaces reuse this: the admin single estimator (Ship From editable,
// defaulting to Rosebery) and the client Rate Browser tab (Ship From locked
// to Rosebery, read-only). The parent owns submission — this component just
// collects a normalised payload and hands it back via onSubmit.
//
// Payload shape matches the API contract EXACTLY:
//   { ship_from?, ship_to:{country_code,city_locality,postal_code},
//     residential, weight_kg, weight_g, package_code,
//     length_cm, width_cm, height_cm, confirmation }
// The admin caller adds client_id; the client caller drops ship_from (the
// server pins Rosebery regardless).

import { useState } from 'react'
import { Loader2, Search } from 'lucide-react'
import { COUNTRY_OPTIONS } from '../../lib/countries'

// The single source of truth for the fixed OPS 3PL origin. Admin sees this
// pre-filled and editable; the client sees it as read-only text.
export const DEFAULT_ORIGIN = {
  city_locality: 'Rosebery',
  state_province: 'NSW',
  postal_code: '2018',
  country_code: 'AU',
}

// ShipEngine delivery-confirmation levels.
const CONFIRMATION_OPTIONS = [
  { value: 'none',                 label: 'None' },
  { value: 'delivery',             label: 'Delivery' },
  { value: 'signature',            label: 'Signature' },
  { value: 'adult_signature',      label: 'Adult signature' },
]

export default function RateQuoteForm({
  onSubmit,
  loading = false,
  // When true (admin), Ship From is an editable block prefilled with Rosebery.
  // When false (client), Ship From is rendered read-only as the fixed origin.
  editableOrigin = false,
  // Optional left-of-button slot (e.g. admin client selector lives above).
  disabled = false,
}) {
  const [origin, setOrigin] = useState({ ...DEFAULT_ORIGIN })
  const [shipTo, setShipTo] = useState({
    country_code: 'AU',
    city_locality: '',
    postal_code: '',
  })
  const [weightKg, setWeightKg] = useState('0')
  const [weightG, setWeightG]   = useState('500')
  const [dims, setDims] = useState({ length_cm: '', width_cm: '', height_cm: '' })
  const [confirmation, setConfirmation] = useState('none')

  const num = (v) => {
    const n = parseFloat(v)
    return Number.isFinite(n) ? n : 0
  }

  function handleSubmit(e) {
    e.preventDefault()
    const payload = {
      ship_to: {
        country_code:  shipTo.country_code,
        city_locality: shipTo.city_locality.trim(),
        postal_code:   shipTo.postal_code.trim(),
      },
      residential: true,        // always residential (field removed from the form)
      weight_kg:   num(weightKg),
      weight_g:    num(weightG),
      package_code: 'package',  // always custom / carrier-default package
      length_cm:   num(dims.length_cm),
      width_cm:    num(dims.width_cm),
      height_cm:   num(dims.height_cm),
      confirmation,
    }
    // Admin surface sends an (editable) origin; client surface omits it and
    // the server pins Rosebery.
    if (editableOrigin) {
      payload.ship_from = {
        city_locality:  origin.city_locality.trim(),
        state_province: origin.state_province.trim(),
        postal_code:    origin.postal_code.trim(),
        country_code:   origin.country_code.trim(),
      }
    }
    onSubmit(payload)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Ship From */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Ship From</p>
        {editableOrigin ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="label">Suburb / City</label>
              <input
                className="input"
                value={origin.city_locality}
                onChange={e => setOrigin(o => ({ ...o, city_locality: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">State</label>
              <input
                className="input"
                value={origin.state_province}
                onChange={e => setOrigin(o => ({ ...o, state_province: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Postcode</label>
              <input
                className="input"
                value={origin.postal_code}
                onChange={e => setOrigin(o => ({ ...o, postal_code: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Country</label>
              <input
                className="input"
                value={origin.country_code}
                onChange={e => setOrigin(o => ({ ...o, country_code: e.target.value.toUpperCase() }))}
                maxLength={2}
              />
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
            {DEFAULT_ORIGIN.city_locality} {DEFAULT_ORIGIN.state_province} {DEFAULT_ORIGIN.postal_code},{' '}
            {DEFAULT_ORIGIN.country_code}
            <span className="text-xs text-slate-400 ml-2">(OPS 3PL warehouse — fixed origin)</span>
          </div>
        )}
      </div>

      {/* Ship To */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Ship To</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <label className="label">Country</label>
            <select
              className="input"
              value={shipTo.country_code}
              onChange={e => setShipTo(s => ({ ...s, country_code: e.target.value }))}
            >
              {COUNTRY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Suburb / City</label>
            <input
              className="input"
              value={shipTo.city_locality}
              onChange={e => setShipTo(s => ({ ...s, city_locality: e.target.value }))}
              placeholder="Sydney"
            />
          </div>
          <div>
            <label className="label">Postcode</label>
            <input
              className="input"
              value={shipTo.postal_code}
              onChange={e => setShipTo(s => ({ ...s, postal_code: e.target.value }))}
              placeholder="2000"
              required
            />
          </div>
        </div>
      </div>

      {/* Parcel */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Parcel</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <label className="label">Weight (kg)</label>
            <input
              className="input"
              type="number"
              min="0"
              step="0.001"
              value={weightKg}
              onChange={e => setWeightKg(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Weight (g)</label>
            <input
              className="input"
              type="number"
              min="0"
              step="1"
              value={weightG}
              onChange={e => setWeightG(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Length (cm)</label>
            <input
              className="input"
              type="number"
              min="0"
              step="0.1"
              value={dims.length_cm}
              onChange={e => setDims(d => ({ ...d, length_cm: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">Width (cm)</label>
            <input
              className="input"
              type="number"
              min="0"
              step="0.1"
              value={dims.width_cm}
              onChange={e => setDims(d => ({ ...d, width_cm: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">Height (cm)</label>
            <input
              className="input"
              type="number"
              min="0"
              step="0.1"
              value={dims.height_cm}
              onChange={e => setDims(d => ({ ...d, height_cm: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">Confirmation</label>
            <select
              className="input"
              value={confirmation}
              onChange={e => setConfirmation(e.target.value)}
            >
              {CONFIRMATION_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button type="submit" className="btn-primary" disabled={loading || disabled}>
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
          {loading ? 'Getting quotes…' : 'Get quotes'}
        </button>
      </div>
    </form>
  )
}
